import { useContext, useEffect, useMemo, useCallback, useState } from 'react';
import { BaseNewAndDetail, getDarkThemeClass, defErrProps } from '../../utility';
import { useTheme } from '@emotion/react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import _ItemsTab from './_ItemsTab';
import _CategoryTab from './_CategoryTab';
import { ItemBreadCrumbs } from '../../utility/BreadcrumbLinks';
import { FarmContext } from '../../../context/FarmContext';
import apiService from '../../../api/apiService';

export default function Item(props) {
  const [values, setValues] = useState({
    openItemsPopup: false,
    items: [],
    triggerAddNewItem: false,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const { setFarmContextValue } = useContext(FarmContext);

  const theme = useTheme();
  const { item, integrationid } = useParams();

  const currentTab = item || 'Items';
  const currDarkThemeClass = useMemo(() => getDarkThemeClass(theme), [theme?.palette?.themeMode]);

  const baseEntityName = 'Items';
  const itemsTagKey = 'item';
  const categoryTagKey = 'category';
  const baseSaveBtnChildren = 'Add Item';
  const itemsKey = 'item';

  const tabEntityMap = [
    { key: itemsTagKey, label: 'Items', defaultForDetail: true, validForNew: false },
    { key: categoryTagKey, label: 'Category', defaultForDetail: false, validForNew: false },
  ];

  const navigate = useNavigate();

  const breadcrumbsProps = {
    currLink: currentTab,
    currDarkThemeClass,
    navigate,
  };
  const breadcrumbs = <ItemBreadCrumbs {...breadcrumbsProps} />;

  useEffect(() => {
    const fetchFarms = async () => {
      try {
        const response = await apiService.fetchInstance('farm/?fetch_farms=true');

        setFarmContextValue(response?.data || { farms: [] });
      } catch (error) {
        console.error('Error fetching farms', error);
        setFarmContextValue({ farms: [] }); // fallback
      }
    };
    fetchFarms();
  }, []);

  const handleErrorWithFeedback = feedbackCX => (handleSetValues, errPropsFn) => err => {
    console.log('error occureed');
    handleSetValues('error', true);
    handleSetValues('loading', true);
    const errProps = errPropsFn?.(err) ?? defErrProps;

    feedbackCX?.setContextValue?.(true, errProps);
  };

  const handleSetLoading = prop => value => handleSetValues('loading', { ...values?.loading, [prop]: value });

  const handleEntityEntryCreateRes = prop => res => {
    handleSetLoading(prop)(false);

    const { data } = res;
    const newState = data?.length > 0 ? [...values?.[prop], ...data] : [...values?.[prop], data];

    handleSetValues(prop, newState);

    return Promise.resolve({ success: true });
  };
  const handleEntityEntryModifyRes = prop => res => {
    const { data } = res;

    handleSetValues(
      prop,
      values?.[prop]?.map(e => (e.id === data.id ? data : e))
    );

    return Promise.resolve({ success: true });
  };
  const handleSetValues = useCallback(
    (prop, value) => {
      setValues(state => ({ ...state, [prop]: value }));
    },
    [setValues]
  );

  const toDisable = () => {
    // different toDisable conditions based on tab
    switch (tab) {
      default:
        return false;
    }
  };

  const handleSave = () => {
    if (tab === itemsKey) handleSetValues('triggerAddNewItem', true);
  };

  const getDefaultTabForEndpoint = () => tabEntityMap?.find(tab => tab?.defaultForDetail)?.key;

  const itemTagsprops = {
    currDarkThemeClass,
    values,
    items: values?.items,
    open: values?.openItemsPopup,
    addNew: values?.add ?? false,
    update: values?.update ?? false,
    handleErrorWithFeedback,
    handleEntityEntryCreateRes,
    handleEntityEntryModifyRes,
    handleSetValues,
  };
  const categoryprops = {
    currDarkThemeClass,
    items: values?.itmes,
    openItemsPopup: values?.openItemsPopup ?? false,
    addNew: values?.add ?? false,
    update: values?.update ?? false,
    handleErrorWithFeedback,
    handleEntityEntryCreateRes,
    handleEntityEntryModifyRes,
    handleSetValues,
  };

  const tabContentMap = {
    [itemsTagKey]: <_ItemsTab {...itemTagsprops} />,
    [categoryTagKey]: <_CategoryTab {...categoryprops} />,
  };

  const tabCreateTitleMap = {
    [itemsTagKey]: 'Item',
  };
  const showSaveBtn = tab => Object.keys(tabCreateTitleMap).includes(tab);

  const baseProps = {
    baseEntityName,
    baseid: undefined,
    currDarkThemeClass,
    tabContentMap,
    tabEntityMap,
    breadcrumbs,
    createBaseEntityMsg: '',
    baseSaveBtnChildren,
    baseEndpointCheck: false,
    enablePopup: true,
    showSaveBtn,
    handleSave,
    toDisable,

    getDefaultTabForEndpoint,
  };

  return <BaseNewAndDetail {...baseProps} />;
}
